/**
 * Instantly place a widget at the next available canvas position.
 * Mirrors the pattern of useCanvasAddContextPlacement for entities —
 * no placement mode required, no canvas click needed.
 */

import type { WidgetPreset } from "@/pages/canvas/nodes/widgets";
import type { Node } from "@xyflow/react";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";

/** The node a preset becomes at a flow position — shared with placement mode. */
export function buildWidgetNode(
  preset: WidgetPreset,
  position: { x: number; y: number },
): Node {
  return {
    id: `widget-${uuidv4()}`,
    type: "widgetNode",
    position,
    // A component widget gets no height: React Flow measures the DOM
    // first, then the shell syncs the real height to storage. Stickers
    // know their size.
    width: preset.defaultWidth,
    ...(preset.component ? {} : { height: preset.defaultHeight }),
    data: {
      label: preset.label,
      presetId: preset.id,
      ...(preset.initialData ?? {}),
    },
  };
}

export function useCanvasWidgetQuickPlace() {
  const { addNode } = useCanvas();
  const { getNextNodePosition } = useCanvasNodePlacement();

  const placeWidget = useCallback(
    (preset: WidgetPreset, flowPosition?: { x: number; y: number }) => {
      const position =
        flowPosition ??
        getNextNodePosition(preset.defaultWidth, preset.defaultHeight);
      addNode(
        buildWidgetNode(preset, position) as Parameters<typeof addNode>[0],
      );
    },
    [getNextNodePosition, addNode],
  );

  return { handleQuickPlaceWidget: placeWidget, placeWidget };
}
